import React, { useState, useEffect, useRef } from 'react';
import { captureDashboardPdf, loadPdfImage } from './dashboardPdf';
import moment from 'moment';
import { css } from '@emotion/css';
import ApplicationItem from './ApplicationItem';
import AssistantGroupList from './AssistantGroupList';
import AssistantAnalyticsFilter from './AssistantAnalyticsFilter';
import useFilterDismissal from './useFilterDismissal';
import ApplicationLayoutModel from '../../../../../shared/models/applications/api/ApplicationLayoutModel';
import { getApplicationLayout, updateApplicationLayout } from '../../../../../shared/api/controllers/applications';
import { normalizeApplicationLayout, getVisibleGroupAppIds, getSelectedAppIds, getCategorySearchAppIds, setCategoryAppSelection, shouldReloadApplicationLayout } from './assistantGroupUtils';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';
import { voicify_spark_darker } from '../../../../../shared/constants/colors';
import { breakpoint_small } from '../../../../../shared/constants/breakpoints';
import ApplicationWithMembers from '../../../../../shared/models/applications/api/ApplicationWithMembers';

// Import dashboard components from separate files
import { DentalSchedulingDashboardWithProvider } from './DentalSchedulingDashboard';
import { AIOrderingDashboardWithProvider } from './AIOrderingDashboard';
import { AIAnsweringDashboard } from './AIAnsweringDashboard';

// Import styled components from the centralized styles file (only what's needed for the main component)
import {
    DashboardWrapper,
    NotificationStack,
    LeftSidePanel,
    CollapsedSidebarContent,
    CollapsedAvatarWrapper,
    CollapsedAvatarRing,
    ExpandSidebarButton,
    ExpandArrowIcon,
    CollapseSidebarButton,
    CollapseArrowIcon,
    CollapsedHeader,
    CollapsedDateRangeText,
    ShareButtonWrapper,
    ShareButton,
    ShareIcon,
    ShareDropdownArrow,
    ShareDropdownMenu,
    ShareDropdownItem,
    ShareDropdownIcon,
    BottomGraphic,
    OrgAvatarSection,
    VerticalAccentDecoration,
    OrgName,
    UpgradeModalOverlay,
    UpgradeModalContent,
    UpgradeModalCloseButton,
    UpgradeModalBody,
    PlanSection,
    PlanLabel,
    PlanName,
    PlanTag,
    PlanDescription,
    UpgradeDivider,
    UpgradeModalButton,
    DatePickerSection,
    LocationsSection,
    LocationsHeaderWrapper,
    LocationsTitleGroup,
    LocationsTitle,
    SelectedLocationsCount,
    SelectionControls,
    SelectionButton,
    SelectionDivider,
    FilterIconWrapper,
    FilterIconButton,
    FilterIconSvg,
    FilterDropdown,
    SpanishIndicatorBadge,
    AssistantSearchWrapper,
    AssistantSearchInput,
    AssistantSearchClear,
    NoResultsText,
    LocationsScrollContainer,
    SidebarLoaderWrapper,
    MainContent,
    MainContentLoaderWrapper,
    EmptyStateMessage,
    MobileAssistantToggle,
    MobileToggleText,
    MobileToggleIcon,
    CollapsibleContent,
    AvatarScaleWrapper,
    RefreshButton
} from './ApplicationDashboard.styles';
import { AssistantListToolbar, OrganizeButton, LayoutStatus } from './ApplicationDashboard.styles';

export interface ApplicationModelWithPhoneNumberAndStatus extends ApplicationWithMembers {
    phoneNumber?: string
    virtualNumber?: string
    hasIncompleteFormFields?: boolean
    isOutboundEnabled?: boolean
}

const sortAppsByLiveStatusAndName = (apps: ApplicationModelWithPhoneNumberAndStatus[]): ApplicationModelWithPhoneNumberAndStatus[] => {
    return [...apps].sort((a, b) => {
        if (a.isLive !== b.isLive) {
            return a.isLive ? -1 : 1;
        }

        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });
};

export type ApplicationType = "common" | "location";

import AnalyticsInterval from '../../../../../shared/models/analytics/AnalyticsInterval';
import TelephonyDeploymentContainer from '../../../../../shared/hooks/TelephonyDeploymentContainer';
import DashboardDatePicker from './DashboardDatePicker';
import { aiAnsweringCommonAppName, aiDentalSchedulingCommonAppName, aiOrderingCommonAppName } from '../../../../../shared/constants/sparkConstants/moduleConsts';
import { HealthcareSchedulingFeatureFlagId, FoodOrderingFeatureFlagId, GenAIFoodOrdering, OutboundCallFeatureFlagId } from '../../../../../shared/constants/featureFlagIds';
import UserAvatar from '../../../../../shared/components/general/UserAvatar';
import SecondaryLoader from '../../../../../shared/components/general/SecondaryLoader';
import Loader from '../../../../../shared/components/general/Loader';
import { AppNameMap } from '../../../../../shared/utils/engagementAnalyticsTransformers';
import OrganizationModel from '../../../../../shared/models/organizations/api/OrganizationModel';
import PageError from '../../../../../shared/components/general/PageError';
import MediaContainer from '../../../../../shared/state/containers/MediaContainer';
import OrganizationAvatarUpload from '../../../../../shared/components/general/OrganizationAvatarUpload';
import { consolidateConfigurationWarnings } from './dashboardWarningUtils';

const voicifyLogo = require('../../../../../shared/content/images/logo.svg');

const modernTooltipStyle = css`
  .rc-tooltip-inner {
    font-family: Muli, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.3px;
    line-height: 1.4;
    z-index: 120;
    color: white;
    background: linear-gradient(135deg, ${voicify_spark_darker} 0%, #1a4d6b 100%);
    box-shadow: 0 4px 12px rgba(30, 126, 185, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    padding: 6px 10px;
    min-height: 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  .rc-tooltip-arrow {
    display: none;
  }
`;

const refreshTooltipStyle = css`
  .rc-tooltip-inner {
    font-family: Muli, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.3px;
    line-height: 1.4;
    z-index: 120;
    color: white;
    background: linear-gradient(135deg, ${voicify_spark_darker} 0%, #1a4d6b 100%);
    box-shadow: 0 4px 12px rgba(30, 126, 185, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    padding: 6px 10px;
    min-height: 0;
    max-width: 200px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  .rc-tooltip-arrow {
    display: none;
  }
  ${breakpoint_small} {
    display: none;
  }
`;

interface ApplicationDashboardProps {
    organizationId: string;
    appsContainer: any;
    orgContainer: any;
    appliedAppTemplateContainer: any;
    facetContainer: any;
    templateConfigurationContainer: any;
    userContainer: any;
    mediaContainer: MediaContainer;
    history: any;
}

type BusinessVertical = 'dental-scheduling' | 'ai-answering' | 'ai-ordering';

type SparkOnboardingCueStage = 'shared-config';

interface SparkOnboardingCueState {
    organizationId: string;
    stage: SparkOnboardingCueStage;
}

const sparkOnboardingCueKey = 'SPARK_ONBOARDING_CUE';
const sparkOnboardingCueDismissedKey = 'SPARK_ONBOARDING_CUE_DISMISSED';

const getSparkOnboardingCueDismissalKey = (organizationId: string, stage: SparkOnboardingCueStage) => `${organizationId}:${stage}`;

const getSparkOnboardingCue = (): SparkOnboardingCueState | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const storedCue = localStorage.getItem(sparkOnboardingCueKey);
    if (!storedCue) {
        return null;
    }

    try {
        return JSON.parse(storedCue) as SparkOnboardingCueState;
    } catch (_) {
        localStorage.removeItem(sparkOnboardingCueKey);
        return null;
    }
};

const clearSparkOnboardingCue = () => {
    if (typeof window === 'undefined') {
        return;
    }

    localStorage.removeItem(sparkOnboardingCueKey);
};

const getSparkOnboardingCueDismissals = (): string[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    const storedDismissals = localStorage.getItem(sparkOnboardingCueDismissedKey);
    if (!storedDismissals) {
        return [];
    }

    try {
        const parsedDismissals = JSON.parse(storedDismissals);
        return Array.isArray(parsedDismissals) ? parsedDismissals : [];
    } catch (_) {
        localStorage.removeItem(sparkOnboardingCueDismissedKey);
        return [];
    }
};

const setSparkOnboardingCueDismissals = (dismissals: string[]) => {
    if (typeof window === 'undefined') {
        return;
    }

    localStorage.setItem(sparkOnboardingCueDismissedKey, JSON.stringify(dismissals));
};

const ApplicationDashboard: React.FC<ApplicationDashboardProps> = (props) => {
    const [businessVertical, setBusinessVertical] = useState<BusinessVertical>('dental-scheduling');
    const [isBusinessVerticalDetermined, setIsBusinessVerticalDetermined] = useState<boolean>(false);
    const [hasOutboundCallApps, setHasOutboundCallApps] = useState<boolean>(false);
    const [commonApps, setCommonApps] = useState<ApplicationModelWithPhoneNumberAndStatus[]>();
    const [locationApps, setLocationApps] = useState<ApplicationModelWithPhoneNumberAndStatus[]>();
    const [applicationLayout, setApplicationLayout] = useState<ApplicationLayoutModel | null>(null);
    const [isOrganizing, setIsOrganizing] = useState(false);
    const [isSavingLayout, setIsSavingLayout] = useState(false);
    const [layoutError, setLayoutError] = useState<string[]>([]);
    const [isLoadingApps, setIsLoadingApps] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<string>(moment().subtract(2, 'days').startOf('day').format('YYYY-MM-DD'));
    // Default to today
    const [endDate, setEndDate] = useState<string>(moment().startOf('day').format('YYYY-MM-DD'));
    const [interval, setInterval] = useState<AnalyticsInterval>('custom');
    const [excludedAppIds, setExcludedAppIds] = useState<Set<string>>(new Set());
    // Track whether we've initialized the default filter for non-live apps
    const [hasInitializedLiveFilter, setHasInitializedLiveFilter] = useState<boolean>(false);
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState<boolean>(false);
    const [isChartsLoading, setIsChartsLoading] = useState<boolean>(false);
    const [isDashboardLoading, setIsDashboardLoading] = useState<boolean>(false);
    const [isAppointmentSummaryLoading, setIsAppointmentSummaryLoading] = useState(false);
    const [assistantSearchQuery, setAssistantSearchQuery] = useState<string>('');
    const [isSpanishOnlyFilter, setIsSpanishOnlyFilter] = useState<boolean>(false);
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState<boolean>(false);
    const closeFilterDropdown = React.useCallback(() => setIsFilterDropdownOpen(false), []);
    const filterDropdownRef = useFilterDismissal(isFilterDropdownOpen, closeFilterDropdown);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
    const [isAssistantListCollapsed, setIsAssistantListCollapsed] = useState<boolean>(false);
    const [isShareDropdownOpen, setIsShareDropdownOpen] = useState<boolean>(false);
    const [copyLinkSuccess, setCopyLinkSuccess] = useState<boolean>(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
    const pdfExportInProgress = useRef(false);
    const [pdfProgress, setPdfProgress] = useState('Preparing PDF...');
    const [pdfErrors, setPdfErrors] = useState<string[]>([]);
    const [avatarUploadErrors, setAvatarUploadErrors] = useState<string[]>([]);
    const [templateLoadErrors, setTemplateLoadErrors] = useState<string[]>([]);
    const [autofillWarnings, setAutofillWarnings] = useState<string[]>([]);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [isRefreshSpinning, setIsRefreshSpinning] = useState<boolean>(false);
    const [dismissedCueKeys, setDismissedCueKeys] = useState<string[]>(() => getSparkOnboardingCueDismissals());
    const [currentCue, setCurrentCue] = useState<SparkOnboardingCueState | null>(() => getSparkOnboardingCue());

    // Callback to receive loading state from child dashboard components
    const handleDashboardLoadingChange = React.useCallback((isLoading: boolean) => {
        setIsDashboardLoading(isLoading);
    }, []);

    // Combined state to disable sidebar controls during any loading state
    // This includes initial loading (before business vertical is determined) and chart loading
    const isSidebarDisabled = isDashboardLoading || !isBusinessVerticalDetermined || isLoadingApps || isGeneratingPdf;
    const isPdfDataLoading = isDashboardLoading || isAppointmentSummaryLoading || isChartsLoading || !isBusinessVerticalDetermined || isLoadingApps;
    const isSearchDisabled = !isBusinessVerticalDetermined || isLoadingApps;

    // True once loading is complete and there are no assistants configured
    const hasNoAssistants = !isLoadingApps && isBusinessVerticalDetermined && (locationApps?.length ?? 0) === 0;

    const dashboardRef = useRef<HTMLDivElement>(null);
    const layoutOrganizationIdRef = useRef(props.organizationId);
    layoutOrganizationIdRef.current = props.organizationId;

    const telephonyDeploymentContainer = TelephonyDeploymentContainer.useContainer();

    const currentOrganization: OrganizationModel = props.orgContainer.state.currentOrganization;
    const currentUser = props.userContainer.state.currentUser;

    useEffect(() => {
        props.templateConfigurationContainer.getOrganizationTemplates(props.organizationId);
        loadApps();
        props.appsContainer.loadFeatureFlags();
        setCurrentCue(getSparkOnboardingCue());
        // Reset the live filter initialization flag when the organization changes
        // so that the filter is re-initialized for the new organization's apps
        setHasInitializedLiveFilter(false);
    }, [props.organizationId]);

    useEffect(() => {
        let active = true;
        setApplicationLayout(null);
        setLayoutError([]);
        setIsOrganizing(false);
        setIsSavingLayout(false);
        getApplicationLayout(props.organizationId).then(result => {
            if (!active) return;
            if (result.resultType === 'Ok') setApplicationLayout(result.data);
            else setLayoutError(['Could not load assistant groups. Try refreshing the dashboard.']);
        });
        return () => { active = false; };
    }, [props.organizationId]);

    useEffect(() => {
        const refreshCueState = () => {
            setCurrentCue(getSparkOnboardingCue());
            setDismissedCueKeys(getSparkOnboardingCueDismissals());
        };

        window.addEventListener('focus', refreshCueState);
        window.addEventListener('storage', refreshCueState);

        return () => {
            window.removeEventListener('focus', refreshCueState);
            window.removeEventListener('storage', refreshCueState);
        };
    }, []);

    useEffect(() => {
        setCurrentCue(getSparkOnboardingCue());
        setDismissedCueKeys(getSparkOnboardingCueDismissals());
    }, [props.organizationId, props.history?.location?.key, props.history?.location?.pathname]);

    // Read warnings passed via router state after form submission, then clear them
    // so they don't reappear on subsequent navigations. Also check if a refresh is requested.
    useEffect(() => {
        const locationState = props.history?.location?.state as any;
        const hasWarningState = !!(
            locationState?.autofillErrors?.length ||
            locationState?.newlySharedFieldTitles?.length ||
            locationState?.unsyncedLocations?.length ||
            locationState?.posConfigErrors?.length ||
            locationState?.phoneProvisioningErrors?.length
        );
        const shouldRefresh = !!locationState?.refresh;

        if (!hasWarningState && !shouldRefresh) return;

        if (shouldRefresh) {
            loadApps(true);
        }

        const warnings = consolidateConfigurationWarnings(locationState);

        if (warnings.length > 0) {
            setAutofillWarnings(warnings);
        }

        if (
            locationState?.autofillErrors ||
            locationState?.newlySharedFieldTitles ||
            locationState?.unsyncedLocations ||
            locationState?.syncTimedOutLocations ||
            locationState?.posConfigErrors ||
            locationState?.phoneProvisioningErrors
        ) {
            props.history.replace({ ...props.history.location, state: {} });
        }
    }, [props.history?.location?.key, props.history?.location?.state]);

    // Watch for updates to apps in the container (e.g., isLive toggle) and sync local state
    useEffect(() => {
        const orgApps = props.appsContainer.state.orgApplicationsList;
        if (orgApps?.length > 0) {
            // Sync locationApps
            if (locationApps?.length > 0) {
                const updatedLocationApps = locationApps.map(app => {
                    const updatedApp = orgApps.find(a => a.id === app.id);
                    if (updatedApp) {
                        // Only update specific properties that can change, preserving locally-fetched data
                        // (phoneNumber, virtualNumber, hasIncompleteFormFields are fetched separately)
                        return { ...app, isLive: updatedApp.isLive, name: updatedApp.name };
                    }
                    return app;
                });
                const hasLocationChanges = updatedLocationApps.some((app, idx) => {
                    const original = locationApps[idx];
                    return app.isLive !== original.isLive || app.name !== original.name;
                });
                if (hasLocationChanges) {
                    setLocationApps(sortAppsByLiveStatusAndName(updatedLocationApps));
                }
            }
            // Sync commonApps
            if (commonApps?.length > 0) {
                const updatedCommonApps = commonApps.map(app => {
                    const updatedApp = orgApps.find(a => a.id === app.id);
                    if (updatedApp) {
                        // Only update specific properties that can change, preserving locally-fetched data
                        // (phoneNumber, virtualNumber, hasIncompleteFormFields are fetched separately)
                        return { ...app, isLive: updatedApp.isLive, name: updatedApp.name };
                    }
                    return app;
                });
                const hasCommonChanges = updatedCommonApps.some((app, idx) => {
                    const original = commonApps[idx];
                    return app.isLive !== original.isLive || app.name !== original.name;
                });
                if (hasCommonChanges) {
                    setCommonApps(sortAppsByLiveStatusAndName(updatedCommonApps));
                }
            }
        }
    }, [props.appsContainer.state.orgApplicationsList]);

    // Determine business vertical from a set of location apps and update state immediately.
    // Called eagerly inside loadApps() as soon as apps are fetched so that the vertical
    // dashboard mounts and begins analytics in parallel with template loading.
    const determineAndSetBusinessVertical = (apps: ApplicationModelWithPhoneNumberAndStatus[]) => {
        const hasFoodOrderingApps = apps.some(app =>
            app.featureFlags?.some(ff =>
                ff.id && (ff.id === FoodOrderingFeatureFlagId || ff.id === GenAIFoodOrdering)
            )
        );
        const hasHealthcareApps = apps.some(app =>
            app.featureFlags?.some(ff => ff.id && ff.id === HealthcareSchedulingFeatureFlagId)
        );

        const hasOutboundApps = apps.some(app =>
            app.featureFlags?.some(ff => ff.id && ff.id === OutboundCallFeatureFlagId)
        );
        setHasOutboundCallApps(hasOutboundApps);

        setEndDate(moment().endOf('day').format('YYYY-MM-DD'));

        // Prioritize food ordering > healthcare > ai-answering
        if (hasFoodOrderingApps) {
            setBusinessVertical('ai-ordering');
        } else if (hasHealthcareApps) {
            setBusinessVertical('dental-scheduling');
        } else {
            setBusinessVertical('ai-answering');
        }
        setIsBusinessVerticalDetermined(true);
    };

    // Clear Spanish-only filter when switching to a non-multilingual organization
    useEffect(() => {
        if (currentOrganization?.isMultiLingual === false && isSpanishOnlyFilter) {
            setIsSpanishOnlyFilter(false);
            setIsFilterDropdownOpen(false);
        }
    }, [currentOrganization?.isMultiLingual, isSpanishOnlyFilter]);

    // Initialize excludedAppIds to exclude non-live apps by default
    // This ensures only isLive==true apps are included in the filter initially
    // Delay initialization until apps have finished loading so we don't miss
    // non-live apps that arrive in later chunks.
    useEffect(() => {
        if (
            !isLoadingApps &&
            locationApps &&
            locationApps.length > 0 &&
            !hasInitializedLiveFilter
        ) {
            const nonLiveAppIds = new Set(
                locationApps.filter(app => !app.isLive).map(app => app.id)
            );
            setExcludedAppIds(nonLiveAppIds);
            setHasInitializedLiveFilter(true);
        }
    }, [locationApps, hasInitializedLiveFilter, isLoadingApps]);

    const loadApps = async (forceRefresh: boolean = false) => {
        setTemplateLoadErrors([]);
        try {
            // Check if we already have cached data to avoid expensive reloads
            const cachedApps = props.appsContainer.state.orgApplicationsList;
            const hasPerAppTemplates = props.appliedAppTemplateContainer.state.perApplicationAppliedTemplates?.length > 0;

            // If we have cached apps and templates, use them instead of refetching
            if (!forceRefresh && cachedApps?.length > 0 && hasPerAppTemplates) {
                let newApps: ApplicationModelWithPhoneNumberAndStatus[] = [...cachedApps];

                // Filter out menu apps
                newApps = newApps.filter(app => {
                    const isMenuApp = newApps?.some(a => a.manageMenuApplicationId === app.id) === true ||
                        (app?.name?.toLowerCase()?.startsWith("x -") && app?.name?.toLowerCase()?.endsWith("menu")) ||
                        app?.name?.toLowerCase()?.includes("manage menu") ||
                        app?.name?.toLowerCase()?.includes("manage entities");
                    return !isMenuApp;
                });

                // Update hasIncompleteFormFields from cached template data
                for (let newApp of newApps) {
                    const appAppliedTemplates = props.appliedAppTemplateContainer.state.perApplicationAppliedTemplates.find(
                        appliedTemplate => appliedTemplate.applicationId === newApp.id
                    );
                    if (appAppliedTemplates?.appliedTemplates) {
                        newApp.hasIncompleteFormFields = appAppliedTemplates.appliedTemplates.some(template => {
                            const incompleteSections = props.appliedAppTemplateContainer.getIncompleteSections(template) ?? [];
                            return incompleteSections.length > 0;
                        });
                    }

                    // Get phone numbers from cached telephony deployments
                    const cachedDeployments = telephonyDeploymentContainer.telephonyDeployments?.filter(
                        td => td.applicationId === newApp.id
                    );
                    if (cachedDeployments?.length > 0) {
                        newApp.phoneNumber = cachedDeployments[0].phoneNumber;
                        newApp.virtualNumber = cachedDeployments[0].virtualNumber;
                        newApp.isOutboundEnabled = cachedDeployments.some(td => td.useForOutboundCalling);
                    }
                }

                newApps = sortAppsByLiveStatusAndName(newApps);

                const appsThatAllowTemplating = newApps.filter(app => app.allowsTemplating);
                const appsThatDontAllowTemplating = newApps.filter(app => !app.allowsTemplating);

                setCommonApps(appsThatAllowTemplating);
                setLocationApps(appsThatDontAllowTemplating);
                determineAndSetBusinessVertical(appsThatDontAllowTemplating);
                return;
            }

            setIsLoadingApps(true);
            const appsResult = await props.appsContainer.searchForOrgApplications(
                props.organizationId,
                1,
                30,
                "CreatedDateDescending",
                "",
                true
            );

            let newApps: ApplicationModelWithPhoneNumberAndStatus[] = appsResult?.data?.applications ?? [];

            newApps = newApps.filter(app => {
                const isMenuApp = newApps?.some(a => a.manageMenuApplicationId === app.id) === true ||
                    (app?.name?.toLowerCase()?.startsWith("x -") && app?.name?.toLowerCase()?.endsWith("menu")) ||
                    app?.name?.toLowerCase()?.includes("manage menu") ||
                    app?.name?.toLowerCase()?.includes("manage entities");
                return !isMenuApp;
            });

            // Split apps eagerly so we can determine the business vertical right away.
            // This lets the vertical dashboard (dental/ordering) mount and kick off analytics
            // in parallel with template loading below, instead of waiting for all templates first.
            const earlyLocationApps = sortAppsByLiveStatusAndName(newApps.filter(app => !app.allowsTemplating));
            const earlyCommonApps = sortAppsByLiveStatusAndName(newApps.filter(app => app.allowsTemplating));
            setLocationApps(earlyLocationApps);
            setCommonApps(earlyCommonApps);
            determineAndSetBusinessVertical(earlyLocationApps);

            // Kick off all telephony deployment fetches in parallel (unchanged behaviour).
            const telephonyDeploymentLoadTasks = newApps.map(app =>
                telephonyDeploymentContainer.getTelephonyDeployments(app.id)
            );

            // Load applied templates 10 at a time. After each chunk resolves we recompute
            // hasIncompleteFormFields for all loaded apps and push a sidebar update so status
            // indicators fill in progressively rather than all at once at the very end.
            for (let i = 0; i < newApps.length; i += 10) {
                const chunk = newApps.slice(i, i + 10);
                await Promise.all(
                    chunk.map(app =>
                        props.appliedAppTemplateContainer.loadAppliedTemplates(app.id).catch((err: unknown) => {
                            const message = err instanceof Error ? err.message : `Failed to load templates for app ${app.id}`;
                            setTemplateLoadErrors(prev => [...prev, message]);
                        })
                    )
                );

                for (let newApp of newApps) {
                    const appAppliedTemplates = props.appliedAppTemplateContainer.state.perApplicationAppliedTemplates.find(
                        appliedTemplate => appliedTemplate.applicationId === newApp.id
                    );
                    if (appAppliedTemplates?.appliedTemplates) {
                        newApp.hasIncompleteFormFields = appAppliedTemplates.appliedTemplates.some(template => {
                            const incompleteSections = props.appliedAppTemplateContainer.getIncompleteSections(template) ?? [];
                            return incompleteSections.length > 0;
                        });
                    }
                }

                // Re-sort and push updated sidebar state after each chunk
                const updatedApps = sortAppsByLiveStatusAndName(newApps);
                setLocationApps(updatedApps.filter(app => !app.allowsTemplating));
                setCommonApps(updatedApps.filter(app => app.allowsTemplating));
            }

            const telephonyDeploymentResults = await Promise.all(telephonyDeploymentLoadTasks);

             for (const depResult of telephonyDeploymentResults) {
                if (depResult?.data?.length) {
                    const appWithDep = newApps.find(app => app.id === depResult.data[0].applicationId);
                    if (appWithDep) {
                        appWithDep.phoneNumber = depResult.data[0].phoneNumber;
                        appWithDep.virtualNumber = depResult.data[0].virtualNumber;
                        appWithDep.isOutboundEnabled = depResult.data.some((d: any) => d.useForOutboundCalling);
                    }
                }
            }

            newApps = sortAppsByLiveStatusAndName(newApps);

            const appsThatAllowTemplating = newApps.filter(app => app.allowsTemplating);
            const appsThatDontAllowTemplating = newApps.filter(app => !app.allowsTemplating);

            setCommonApps(appsThatAllowTemplating);
            setLocationApps(appsThatDontAllowTemplating);
        } catch (error) {
            console.error("[loadApps] Error loading apps:", error);
        } finally {
            setIsLoadingApps(false);
        }
    };

    const handleDateRangeChange = (newStartDate: string, newEndDate: string, newInterval: AnalyticsInterval) => {
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setInterval(newInterval);
        // Demo mode: No analytics API calls
    };

    const handleRefreshAnalytics = () => {
        if (isSidebarDisabled) return;
        setRefreshKey(prev => prev + 1);
        setIsRefreshSpinning(true);
        setTimeout(() => setIsRefreshSpinning(false), 600);
    };

    const formatDateRange = () => {
        return `${moment(startDate).format('MMM D')} - ${moment(endDate).format('MMM D')}`;
    };

    const toggleAppFilter = (appId: string) => {
        setExcludedAppIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(appId)) {
                newSet.delete(appId);
            } else {
                newSet.add(appId);
            }
            return newSet;
        });
    };

    const layoutForView = React.useMemo((): ApplicationLayoutModel => {
        return normalizeApplicationLayout(applicationLayout, (locationApps || []).map(app => app.id));
    }, [applicationLayout, locationApps]);

    const appById = React.useMemo(() => new Map((locationApps || []).map(app => [app.id, app])), [locationApps]);
    const loadedAppIds = React.useMemo(() => new Set(appById.keys()), [appById]);
    const groupAppIds = React.useMemo(() => getVisibleGroupAppIds(layoutForView, 'all', loadedAppIds), [layoutForView, loadedAppIds]);
    const groupAppIdsKey = React.useMemo(() => Array.from(groupAppIds).sort().join(','), [groupAppIds]);

    const selectAllApps = () => {
        setExcludedAppIds(previous => {
            const next = new Set(previous);
            groupAppIds.forEach(id => next.delete(id));
            return next;
        });
    };

    const deselectAllApps = () => {
        setExcludedAppIds(previous => new Set([...previous, ...Array.from(groupAppIds)]));
    };

    const saveLayout = async (next: ApplicationLayoutModel) => {
        if (!applicationLayout?.canEdit || isSavingLayout) return;
        const previous = applicationLayout;
        const organizationId = props.organizationId;
        setApplicationLayout(next);
        setIsSavingLayout(true);
        setLayoutError([]);
        const result = await updateApplicationLayout(organizationId, next);
        if (layoutOrganizationIdRef.current !== organizationId) return;
        if (result.resultType === 'Ok') {
            setApplicationLayout(result.data);
        } else {
            setApplicationLayout(previous);
            if (shouldReloadApplicationLayout(result)) {
                const latest = await getApplicationLayout(organizationId);
                if (layoutOrganizationIdRef.current !== organizationId) return;
                if (latest.resultType === 'Ok') {
                    setApplicationLayout(latest.data);
                    setLayoutError([result.httpStatus === 409
                        ? 'Someone else changed the assistant groups. The latest layout has been loaded.'
                        : 'The assistant list changed. The latest layout has been loaded. Refresh to see any new assistants.']);
                } else {
                    setLayoutError(['The assistant layout changed. Refresh the dashboard before editing again.']);
                    setIsOrganizing(false);
                }
            } else {
                setLayoutError(['Could not save assistant groups. Your last change was undone.']);
            }
        }
        setIsSavingLayout(false);
    };

    const toggleOrganize = () => {
        if (!applicationLayout?.canEdit || isSavingLayout) return;
        if (!isOrganizing) {
            setAssistantSearchQuery('');
            setIsSpanishOnlyFilter(false);
            setIsFilterDropdownOpen(false);
        }
        setIsOrganizing(!isOrganizing);
    };

    // Convert excludedAppIds to selectedAppIds for components that expect selected IDs
    // Memoized to prevent unnecessary re-renders and duplicate API calls
    const selectedAppIds = React.useMemo(() => getSelectedAppIds(groupAppIds, excludedAppIds), [groupAppIdsKey, excludedAppIds]);
    const hasAnalyticsSelectionFilter = selectedAppIds.size < groupAppIds.size;
    const selectCategoryApps = (appIds: string[], selected: boolean) => {
        if (isSidebarDisabled || isOrganizing) return;
        setExcludedAppIds(previous => setCategoryAppSelection(previous, appIds, selected));
    };

    // Create app name map from locationApps for resolving app IDs to names in tooltips
    const appNameMap = React.useMemo((): AppNameMap => {
        const map: AppNameMap = {};
        locationApps?.forEach(app => {
            if (app.id && app.name) {
                map[app.id] = app.name;
            }
        });
        return map;
    }, [locationApps]);

    const selectedAppLiveStatusMap = React.useMemo((): Record<string, boolean> => {
        const map: Record<string, boolean> = {};
        locationApps?.forEach(app => {
            if (app.id) {
                map[app.id] = app.isLive === true;
            }
        });
        return map;
    }, [locationApps]);

    const categorySearchAppIds = React.useMemo(() => getCategorySearchAppIds(layoutForView, assistantSearchQuery), [layoutForView, assistantSearchQuery]);

    // Search assistant and group names independently of the analytics selection.
    const filteredLocationApps = locationApps?.filter(app => {
        const matchesSearch = app.name.toLowerCase().includes(assistantSearchQuery.trim().toLowerCase()) || categorySearchAppIds.has(app.id);
        return matchesSearch && groupAppIds.has(app.id);
    }) || [];

    const sharedTopicsApp = commonApps?.find(
        c => c.name === aiAnsweringCommonAppName || c.name === aiDentalSchedulingCommonAppName || c.name === aiOrderingCommonAppName
    ) ?? commonApps?.[0];
    const hasSharedConfigCue = currentCue?.organizationId === props.organizationId && currentCue.stage === 'shared-config';
    const isCueDismissed = (stage: SparkOnboardingCueStage) => dismissedCueKeys.includes(getSparkOnboardingCueDismissalKey(props.organizationId, stage));
    const showSharedConfigCue = !!(hasSharedConfigCue && sharedTopicsApp && !isCueDismissed('shared-config'));

    const dismissCue = (stage: SparkOnboardingCueStage) => {
        const dismissalKey = getSparkOnboardingCueDismissalKey(props.organizationId, stage);
        if (dismissedCueKeys.includes(dismissalKey)) {
            return;
        }

        const updatedDismissals = [...dismissedCueKeys, dismissalKey];
        setDismissedCueKeys(updatedDismissals);
        setSparkOnboardingCueDismissals(updatedDismissals);
        // Clear the localStorage cue so it doesn't reappear across sessions
        clearSparkOnboardingCue();
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopyLinkSuccess(true);
            setTimeout(() => setCopyLinkSuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const handleExportPdf = async () => {
        if (!dashboardRef.current || pdfExportInProgress.current || isPdfDataLoading || hasNoAssistants) return;

        pdfExportInProgress.current = true;
        setPdfErrors([]);
        setPdfProgress('Preparing PDF...');

        setIsGeneratingPdf(true);
        setIsShareDropdownOpen(false);

        try {
            const mainContent = dashboardRef.current.querySelector('[data-pdf-content]') as HTMLElement;
            if (!mainContent) {
                throw new Error('Dashboard content not found');
            }

            const { pdf, width } = await captureDashboardPdf(mainContent, (page, total) => {
                setPdfProgress(`Generating PDF ${page}/${total}...`);
            });

            // Add branding in top-right corner (aligned with right edge of charts)
            const padding = 120; // Increased to align with chart edges (60px content padding + 60px for alignment)
            const avatarSize = 80; // Doubled from 40
            const estimatedLogoWidth = 200; // Doubled from 100
            const elementHeight = 80; // Doubled from 40
            const yPosition = 16; // Match share button top position
            const orgName = currentOrganization?.name || 'Dashboard';

            // Calculate total width of branding elements to position from right edge
            // Just avatar + separator + logo (no org name)
            const separatorGap = 24; // Increased for larger elements
            const totalWidth = avatarSize + separatorGap + 1 + separatorGap + estimatedLogoWidth;

            // Position from the right edge, accounting for the content area's left padding (60px when collapsed)
            let currentX = width - padding - totalWidth;

            // Add organization avatar
            try {
                const orgAvatarSrc = currentOrganization?.imageUri;

                if (!orgAvatarSrc) throw new Error('No organization image URL');
                const avatarImg = await loadPdfImage(orgAvatarSrc);

                // Draw avatar as circular image
                const avatarCanvas = document.createElement('canvas');
                avatarCanvas.width = avatarSize * 2;
                avatarCanvas.height = avatarSize * 2;
                const avatarCtx = avatarCanvas.getContext('2d');
                if (avatarCtx) {
                    avatarCtx.scale(2, 2);
                    // Create circular clip
                    avatarCtx.beginPath();
                    avatarCtx.arc(avatarSize / 2, avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
                    avatarCtx.closePath();
                    avatarCtx.clip();
                    avatarCtx.drawImage(avatarImg, 0, 0, avatarSize, avatarSize);

                    const avatarData = avatarCanvas.toDataURL('image/png');
                    pdf.addImage(avatarData, 'PNG', currentX, yPosition + (elementHeight - avatarSize) / 2, avatarSize, avatarSize);
                    currentX += avatarSize + separatorGap;
                }
            } catch (avatarErr) {
                console.warn('Could not add org avatar to PDF:', avatarErr);
            }

            // Add Voicify logo - positioned at the same location as Share button (right: 60px)
            try {
                const logoImg = await loadPdfImage(voicifyLogo);

                // Calculate dimensions preserving aspect ratio
                const targetHeight = 64; // Doubled from 32
                const naturalWidth = logoImg.naturalWidth || logoImg.width || 200;
                const naturalHeight = logoImg.naturalHeight || logoImg.height || 50;
                const aspectRatio = naturalWidth / naturalHeight;
                const actualLogoHeight = targetHeight;
                const actualLogoWidth = targetHeight * aspectRatio;

                // Position logo at Share button location (right: 60px from edge, scaled for canvas)
                const logoRightOffset = 60 * 2; // Scale factor of 2 from html2canvas
                const logoX = width - logoRightOffset - actualLogoWidth;

                // Render SVG to canvas to convert to raster image for PDF
                const logoCanvas = document.createElement('canvas');
                const scale = 3; // Higher scale for better quality
                logoCanvas.width = actualLogoWidth * scale;
                logoCanvas.height = actualLogoHeight * scale;
                const logoCtx = logoCanvas.getContext('2d');
                if (logoCtx) {
                    logoCtx.scale(scale, scale);
                    logoCtx.drawImage(logoImg, 0, 0, actualLogoWidth, actualLogoHeight);
                    const logoData = logoCanvas.toDataURL('image/png');
                    pdf.addImage(logoData, 'PNG', logoX, yPosition + (elementHeight - actualLogoHeight) / 2, actualLogoWidth, actualLogoHeight);
                }
            } catch (logoErr) {
                console.warn('Could not add Voicify logo to PDF:', logoErr);
            }

            const dateStr = moment().format('YYYY-MM-DD');
            await pdf.save(`${orgName}-Dashboard-${dateStr}.pdf`, { returnPromise: true });
        } catch (err) {
            console.error('Failed to generate PDF:', err);
            setPdfErrors(['Could not download the dashboard PDF. Please try again after the dashboard finishes loading.']);
        } finally {
            pdfExportInProgress.current = false;
            setIsGeneratingPdf(false);
        }
    };

    const toggleSidebar = () => {
        if (pdfExportInProgress.current) return;
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const clearAvatarUploadErrors = () => {
        setAvatarUploadErrors([]);
    };

    const clearTemplateLoadErrors = () => {
        setTemplateLoadErrors([]);
    };

    // Get the current avatar URL from organization
    const getCurrentAvatarUrl = () => {
        return currentOrganization?.imageUri || null;
    };

    // Get initials from organization name (first letter of each word)
    const getOrgInitials = () => {
        if (!currentOrganization?.name) return 'ORG';
        return currentOrganization.name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 3);
    };

    return (
        <DashboardWrapper ref={dashboardRef}>
            <NotificationStack>
                <PageError errors={pdfErrors} onClear={() => setPdfErrors([])} />
                <PageError
                    errors={avatarUploadErrors}
                    onClear={clearAvatarUploadErrors}
                />
                <PageError
                    errors={templateLoadErrors}
                    onClear={clearTemplateLoadErrors}
                />
                <PageError
                    errors={layoutError}
                    onClear={() => setLayoutError([])}
                />
                <PageError
                    errors={autofillWarnings}
                    onClear={() => setAutofillWarnings([])}
                    type="warning"
                />
            </NotificationStack>
            <LeftSidePanel isCollapsed={isSidebarCollapsed}>
                {isSidebarCollapsed ? (
                    <CollapsedSidebarContent>
                        <CollapsedAvatarWrapper onClick={toggleSidebar} title="Expand sidebar">
                            <CollapsedAvatarRing />
                            <UserAvatar
                                firstName={currentOrganization?.name || 'O'}
                                lastName={''}
                                src={getCurrentAvatarUrl()}
                                size={40}
                                noShadow={true}
                                noImageText={getOrgInitials()}
                            />
                        </CollapsedAvatarWrapper>
                        <ExpandSidebarButton onClick={toggleSidebar} title="Expand sidebar">
                            <ExpandArrowIcon viewBox="0 0 24 24">
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                            </ExpandArrowIcon>
                        </ExpandSidebarButton>
                    </CollapsedSidebarContent>
                ) : (
                    <>
                        <TooltipWrapper text="Refresh to see the latest call data (may take up to 30 min post-call)." replacementContentStyle={refreshTooltipStyle} place="bottom">
                            <RefreshButton
                                onClick={isSidebarDisabled || hasNoAssistants ? undefined : handleRefreshAnalytics}
                                $isDisabled={isSidebarDisabled || hasNoAssistants}
                                $isSpinning={isRefreshSpinning}
                                disabled={isSidebarDisabled || hasNoAssistants}
                            >
                                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12.85 5.45C11.95 3.7 10.1 2.5 8 2.5c-2.35 0-4.3 1.45-5.1 3.5" stroke="currentColor" strokeWidth="1.3" strokeMiterlimit="10" strokeLinecap="round" />
                                    <path d="M3.1 10.5c0.9 1.75 2.75 3 4.9 3 2.35 0 4.3-1.45 5.1-3.5" stroke="currentColor" strokeWidth="1.3" strokeMiterlimit="10" strokeLinecap="round" />
                                    <polyline points="13 2.5 13 5.5 10 5.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                    <polyline points="3 13.5 3 10.5 6 10.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </RefreshButton>
                        </TooltipWrapper>
                        <CollapseSidebarButton onClick={toggleSidebar} title="Collapse sidebar">
                            <CollapseArrowIcon viewBox="0 0 24 24">
                                <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
                            </CollapseArrowIcon>
                        </CollapseSidebarButton>
                        <OrgAvatarSection
                            $variant={isBusinessVerticalDetermined ?
                                (businessVertical === 'ai-ordering' ? 'food-ordering' :
                                    businessVertical === 'dental-scheduling' ? 'healthcare' : 'default')
                                : 'default'
                            }
                        >
                            <VerticalAccentDecoration $variant={isBusinessVerticalDetermined ?
                                (businessVertical === 'ai-ordering' ? 'food-ordering' :
                                    businessVertical === 'dental-scheduling' ? 'healthcare' : 'default')
                                : 'default'
                            } />
                            <AvatarScaleWrapper>
                                <OrganizationAvatarUpload
                                    organizationId={props.organizationId}
                                    organizationName={currentOrganization?.name || ''}
                                    currentImageUri={currentOrganization?.imageUri}
                                    orgContainer={props.orgContainer}
                                    mediaContainer={props.mediaContainer}
                                    size="large"
                                    isLoading={props.orgContainer.state.isLoadingOrgs || !currentOrganization}
                                    onError={setAvatarUploadErrors}
                                    onClearError={clearAvatarUploadErrors}
                                />
                            </AvatarScaleWrapper>
                            {/* TODO: Uncomment when the feature is available */}
                            {/* <StarterBadgeButton onClick={() => setIsUpgradeModalOpen(true)}>Starter</StarterBadgeButton> */}
                            {/* <PremiumBadge>Premium</PremiumBadge> */}
                            <OrgName>{currentOrganization?.name || 'Organization'}</OrgName>
                        </OrgAvatarSection>

                        <DatePickerSection>
                            <DashboardDatePicker
                                startDate={startDate}
                                endDate={endDate}
                                interval={interval}
                                onChange={handleDateRangeChange}
                                enableTimeSelection
                                isDisabled={!isBusinessVerticalDetermined || isLoadingApps || hasNoAssistants || isGeneratingPdf}
                            />
                        </DatePickerSection>

                        {!hasNoAssistants && (
                        <LocationsSection $isCollapsed={isAssistantListCollapsed}>
                            <MobileAssistantToggle
                                $isCollapsed={isAssistantListCollapsed}
                                onClick={() => setIsAssistantListCollapsed(!isAssistantListCollapsed)}
                            >
                                <MobileToggleText>
                                    AI Assistants ({filteredLocationApps?.length || 0})
                                </MobileToggleText>
                                <MobileToggleIcon viewBox="0 0 24 24" $isCollapsed={isAssistantListCollapsed}>
                                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                                </MobileToggleIcon>
                            </MobileAssistantToggle>
                            <CollapsibleContent $isCollapsed={isAssistantListCollapsed}>
                                <LocationsHeaderWrapper>
                                    <LocationsTitleGroup>
                                        <LocationsTitle>AI Assistants <TooltipWrapper text="Live assistants" replacementContentStyle={modernTooltipStyle} place="top"><span style={{ cursor: 'pointer' }}>({locationApps?.filter(app => app.isLive).length || 0})</span></TooltipWrapper></LocationsTitle>
                                        <SelectedLocationsCount>
                                            {isOrganizing ? 'Select assistants to move' : `${selectedAppIds.size} selected`}
                                        </SelectedLocationsCount>
                                    </LocationsTitleGroup>
                                    {!isOrganizing && <SelectionControls>
                                        <TooltipWrapper text="Select all assistants" replacementContentStyle={modernTooltipStyle} place="top">
                                            <SelectionButton
                                                onClick={isSidebarDisabled ? undefined : selectAllApps}
                                                $isDisabled={isSidebarDisabled}
                                                disabled={isSidebarDisabled}
                                            >
                                                Select All
                                            </SelectionButton>
                                        </TooltipWrapper>
                                        <SelectionDivider>|</SelectionDivider>
                                        <TooltipWrapper text="Deselect all assistants" replacementContentStyle={modernTooltipStyle} place="top">
                                            <SelectionButton
                                                onClick={isSidebarDisabled ? undefined : deselectAllApps}
                                                $isDisabled={isSidebarDisabled}
                                                disabled={isSidebarDisabled}
                                            >
                                                Deselect All
                                            </SelectionButton>
                                        </TooltipWrapper>
                                        <SelectionDivider>|</SelectionDivider>
                                        <FilterIconWrapper ref={filterDropdownRef}>
                                            {isSpanishOnlyFilter && (
                                                <SpanishIndicatorBadge
                                                    $isDisabled={isSidebarDisabled || isOrganizing}
                                                    onClick={isSidebarDisabled || isOrganizing ? undefined : () => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                                                    title="Spanish Only filter active"
                                                >
                                                    ES
                                                </SpanishIndicatorBadge>
                                            )}
                                            <TooltipWrapper text="Filter assistants and analytics" replacementContentStyle={modernTooltipStyle} place="top">
                                                <FilterIconButton
                                                    onClick={isSidebarDisabled || isOrganizing ? undefined : () => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                                                    isActive={isSpanishOnlyFilter || hasAnalyticsSelectionFilter}
                                                    $isDisabled={isSidebarDisabled || isOrganizing}
                                                    disabled={isSidebarDisabled || isOrganizing}
                                                    aria-label="Filter assistants and analytics"
                                                    aria-expanded={isFilterDropdownOpen}
                                                >
                                                    <FilterIconSvg viewBox="0 0 24 24" isActive={isSpanishOnlyFilter || hasAnalyticsSelectionFilter} $isDisabled={isSidebarDisabled || isOrganizing}>
                                                        <path d="M3 4h18v2H3V4zm3 7h12v2H6v-2zm3 7h6v2H9v-2z" />
                                                    </FilterIconSvg>
                                                </FilterIconButton>
                                            </TooltipWrapper>
                                            {isFilterDropdownOpen && (
                                                <FilterDropdown>
                                                    <AssistantAnalyticsFilter
                                                        liveAppIds={new Set((locationApps || []).filter(app => app.isLive).map(app => app.id))}
                                                        showSpanishFilter={currentOrganization?.isMultiLingual}
                                                        spanishOnly={isSpanishOnlyFilter}
                                                        onSpanishOnlyChange={setIsSpanishOnlyFilter}
                                                        layout={layoutForView}
                                                        loadedAppIds={groupAppIds}
                                                        selectedAppIds={selectedAppIds}
                                                        disabled={isSidebarDisabled || isOrganizing}
                                                        onSelect={selectCategoryApps}
                                                        onClearAll={() => {
                                                            setExcludedAppIds(new Set());
                                                            setIsSpanishOnlyFilter(false);
                                                            setAssistantSearchQuery('');
                                                        }}
                                                    />
                                                </FilterDropdown>
                                            )}
                                        </FilterIconWrapper>
                                    </SelectionControls>}
                                </LocationsHeaderWrapper>
                                <AssistantListToolbar>
                                    <AssistantSearchWrapper $isDisabled={isSearchDisabled || isOrganizing}>
                                        <AssistantSearchInput
                                            type="text"
                                            placeholder="Search assistants or groups..."
                                            aria-label="Search assistants or groups"
                                            value={assistantSearchQuery}
                                            onChange={(e) => setAssistantSearchQuery(e.target.value)}
                                            disabled={isSearchDisabled || isOrganizing}
                                        />
                                        {assistantSearchQuery && (
                                            <AssistantSearchClear type="button" aria-label="Clear search" disabled={isSearchDisabled || isOrganizing} onClick={() => setAssistantSearchQuery('')}>
                                                ×
                                            </AssistantSearchClear>
                                        )}
                                    </AssistantSearchWrapper>
                                    {applicationLayout?.canEdit && (
                                        <TooltipWrapper text={isOrganizing ? 'Finish editing assistants' : 'Organize groups and reorder assistants'} replacementContentStyle={modernTooltipStyle} place="topRight">
                                            <OrganizeButton
                                                type="button"
                                                $isActive={isOrganizing}
                                                aria-label={isOrganizing ? 'Finish editing assistants' : 'Edit assistant groups and order'}
                                                aria-pressed={isOrganizing}
                                                onClick={toggleOrganize}
                                                disabled={isSavingLayout || isLoadingApps}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    {isOrganizing ? (
                                                        <path d="M5 12l4 4L19 6" />
                                                    ) : (
                                                        <>
                                                            <path d="M15 5l4 4M4 20l4.5-1 12-12a2.8 2.8 0 0 0-4-4l-12 12L4 20z" />
                                                        </>
                                                    )}
                                                </svg>
                                                {isOrganizing ? 'Done' : 'Edit'}
                                            </OrganizeButton>
                                        </TooltipWrapper>
                                    )}
                                </AssistantListToolbar>
                                {isSavingLayout && <LayoutStatus role="status">Saving…</LayoutStatus>}
                                <LocationsScrollContainer>
                                    {!isLoadingApps && <AssistantGroupList
                                        key={props.organizationId}
                                        organizationId={props.organizationId}
                                        layout={layoutForView}
                                        visibleAppIds={isOrganizing ? loadedAppIds : new Set(filteredLocationApps.map(app => app.id))}
                                        selectedGroupId="all"
                                        searchQuery={assistantSearchQuery}
                                        isOrganizing={isOrganizing}
                                        isSaving={isSavingLayout}
                                        tooltipStyle={modernTooltipStyle}
                                        onChange={saveLayout}
                                        renderApp={(appId, moveSelection) => {
                                            const app = appById.get(appId);
                                            return app && <ApplicationItem
                                                organizationId={props.organizationId}
                                                app={app}
                                                appliedAppTemplateContainer={props.appliedAppTemplateContainer}
                                                facetContainer={props.facetContainer}
                                                history={props.history}
                                                applicationType='location'
                                                isInSidePanel={true}
                                                isFilterable={true}
                                                isSelected={moveSelection ? moveSelection.selected : !excludedAppIds.has(app.id)}
                                                isOrganizingSelection={!!moveSelection}
                                                selectionDisabled={moveSelection?.disabled}
                                                onToggleFilter={moveSelection ? moveSelection.onToggle : () => toggleAppFilter(app.id)}
                                                startDate={startDate}
                                                endDate={endDate}
                                                interval={interval}
                                            />;
                                        }}
                                    />}
                                    {!isLoadingApps && filteredLocationApps?.length === 0 && !isOrganizing && assistantSearchQuery && (
                                        <NoResultsText>No assistants match the current filters.</NoResultsText>
                                    )}
                                    {!isLoadingApps && sharedTopicsApp && !assistantSearchQuery && (
                                        <ApplicationItem
                                            organizationId={props.organizationId}
                                            app={sharedTopicsApp}
                                            appliedAppTemplateContainer={props.appliedAppTemplateContainer}
                                            facetContainer={props.facetContainer}
                                            history={props.history}
                                            applicationType='common'
                                            isInSidePanel={true}
                                            isFilterable={false}
                                            isSelected={true}
                                            onToggleFilter={() => { }}
                                            startDate={startDate}
                                            endDate={endDate}
                                            interval={interval}
                                            cueText={showSharedConfigCue ? 'Next: Fill me out' : undefined}
                                            isCueHighlighted={showSharedConfigCue}
                                            onDismissCue={showSharedConfigCue ? () => dismissCue('shared-config') : undefined}
                                        />
                                    )}
                                    {isLoadingApps && isBusinessVerticalDetermined && (
                                        <SidebarLoaderWrapper>
                                            <SecondaryLoader className="grey" />
                                        </SidebarLoaderWrapper>
                                    )}
                                </LocationsScrollContainer>
                            </CollapsibleContent>
                        </LocationsSection>
                        )}
                        <BottomGraphic />
                    </>
                )}
            </LeftSidePanel>

            <MainContent data-pdf-content $isCollapsed={isSidebarCollapsed}>
                {isSidebarCollapsed && (
                    <CollapsedHeader>
                        <CollapsedDateRangeText>
                            {moment(startDate).format('MMM D, YYYY')} - {moment(endDate).format('MMM D, YYYY')}
                        </CollapsedDateRangeText>
                        <ShareButtonWrapper data-share-button>
                            <ShareButton onClick={() => setIsShareDropdownOpen(!isShareDropdownOpen)} disabled={hasNoAssistants || isGeneratingPdf} aria-busy={isGeneratingPdf} aria-expanded={isShareDropdownOpen}>
                                <ShareIcon viewBox="0 0 24 24">
                                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
                                </ShareIcon>
                                <span role={isGeneratingPdf ? 'status' : undefined}>{isGeneratingPdf ? pdfProgress : 'Share'}</span>
                                <ShareDropdownArrow>{isShareDropdownOpen ? '▲' : '▼'}</ShareDropdownArrow>
                            </ShareButton>
                            {isShareDropdownOpen && (
                                <ShareDropdownMenu>
                                    <ShareDropdownItem onClick={handleExportPdf} disabled={isGeneratingPdf || isPdfDataLoading}>
                                        <ShareDropdownIcon viewBox="0 0 24 24">
                                            <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z" />
                                        </ShareDropdownIcon>
                                        {isPdfDataLoading ? 'Loading data...' : 'Download PDF'}
                                    </ShareDropdownItem>
                                </ShareDropdownMenu>
                            )}
                        </ShareButtonWrapper>
                    </CollapsedHeader>
                )}
                {!isBusinessVerticalDetermined && (
                    <MainContentLoaderWrapper>
                        <Loader />
                    </MainContentLoaderWrapper>
                )}

                {isBusinessVerticalDetermined && businessVertical === 'dental-scheduling' && (
                    <DentalSchedulingDashboardWithProvider
                        organizationId={props.organizationId}
                        dateRange={formatDateRange()}
                        startDate={startDate}
                        endDate={endDate}
                        selectedAppIds={selectedAppIds}
                        shouldLoadData={!isLoadingApps && hasInitializedLiveFilter}
                        isChartsLoading={isChartsLoading}
                        totalAppCount={groupAppIds.size || 3}
                        appNameMap={appNameMap}
                        selectedAppLiveStatusMap={selectedAppLiveStatusMap}
                        isMultiLingual={currentOrganization?.isMultiLingual ?? false}
                        isSpanishOnlyFilter={isSpanishOnlyFilter}
                        isOutboundCallApp={hasOutboundCallApps}
                        onLoadingChange={handleDashboardLoadingChange}
                        onAppointmentSummaryLoadingChange={setIsAppointmentSummaryLoading}
                        refreshKey={refreshKey}
                    />
                )}

                {isBusinessVerticalDetermined && businessVertical === 'ai-answering' && locationApps?.length === 0 && (
                    <EmptyStateMessage>
                        <p>No assistants are configured for this organization yet.</p>
                        <p>Head to Settings &gt; Locations to get started.</p>
                    </EmptyStateMessage>
                )}

                {isBusinessVerticalDetermined && businessVertical === 'ai-answering' && (locationApps?.length ?? 0) > 0 && (
                    <AIAnsweringDashboard
                        organizationId={props.organizationId}
                        dateRange={formatDateRange()}
                        startDate={startDate}
                        endDate={endDate}
                        selectedAppIds={selectedAppIds}
                        isChartsLoading={isChartsLoading}
                        totalAppCount={groupAppIds.size || 3}
                        appNameMap={appNameMap}
                        selectedAppLiveStatusMap={selectedAppLiveStatusMap}
                        isMultiLingual={currentOrganization?.isMultiLingual ?? false}
                        isSpanishOnlyFilter={isSpanishOnlyFilter}
                        onLoadingChange={handleDashboardLoadingChange}
                        refreshKey={refreshKey}
                    />
                )}

                {isBusinessVerticalDetermined && businessVertical === 'ai-ordering' && (
                    <AIOrderingDashboardWithProvider
                        organizationId={props.organizationId}
                        dateRange={formatDateRange()}
                        startDate={startDate}
                        endDate={endDate}
                        selectedAppIds={selectedAppIds}
                        isChartsLoading={isChartsLoading}
                        totalAppCount={groupAppIds.size || 3}
                        appNameMap={appNameMap}
                        selectedAppLiveStatusMap={selectedAppLiveStatusMap}
                        isMultiLingual={currentOrganization?.isMultiLingual ?? false}
                        isSpanishOnlyFilter={isSpanishOnlyFilter}
                        onLoadingChange={handleDashboardLoadingChange}
                        refreshKey={refreshKey}
                    />
                )}
            </MainContent>

            {isUpgradeModalOpen && (
                <UpgradeModalOverlay onClick={() => setIsUpgradeModalOpen(false)}>
                    <UpgradeModalContent onClick={(e) => e.stopPropagation()}>
                        <UpgradeModalCloseButton onClick={() => setIsUpgradeModalOpen(false)}>×</UpgradeModalCloseButton>
                        <UpgradeModalBody>
                            <PlanSection>
                                <PlanLabel>You're on</PlanLabel>
                                <PlanName>Starter <PlanTag>Visibility</PlanTag></PlanName>
                                <PlanDescription>
                                    See high-level metrics, upsell performance, question volume, and export all call data to CSV.
                                </PlanDescription>
                            </PlanSection>
                            <UpgradeDivider />
                            <PlanSection>
                                <PlanLabel>Upgrade to</PlanLabel>
                                <PlanName premium>Premium <PlanTag premium>Intelligence</PlanTag></PlanName>
                                <PlanDescription>
                                    Get everything in Starter — plus AI-driven insights: smarter upsell recommendations, deeper question analysis, and debugger-mode exports that reveal what's really driving results.
                                </PlanDescription>
                            </PlanSection>
                            <UpgradeModalButton onClick={() => setIsUpgradeModalOpen(false)}>
                                Upgrade to Premium
                            </UpgradeModalButton>
                        </UpgradeModalBody>
                    </UpgradeModalContent>
                </UpgradeModalOverlay>
            )}
        </DashboardWrapper>
    );
};

export default ApplicationDashboard;
